import { Button, Flex, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { requiredFieldFormValidator } from "../../../utils/validators/formValidators";
import { useForm } from "@mantine/form";
import { useVerifyDiscountCode } from "../../../hooks/discounts/mutations";
import { IDiscountCode } from "../../../interfaces/Store";
import { useFormContext } from "react-hook-form";
import { recalculateOrderProductsWithLineIds } from "../utils/recalculateOrderProducts";
import { computeBillableOrderSubtotal } from "../utils/minimumBillableQuantity";
const AddDiscountCodeModal = ({ opened, onClose, storeDiscounts }: { opened: boolean, onClose: () => void, storeDiscounts: any }) => {
    const { t } = useTranslation(['NewOrder', 'FieldName', 'FormValidator', 'DiscountsPage']);
    const { setValue, getValues } = useFormContext();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const searchParamStoreId = searchParams.get('storeId') || '';
    const verifyDiscountCode = useVerifyDiscountCode(
        searchParamStoreId
    );
    const form = useForm({
        initialValues: {
            discountCode: ''
        },
        validate: {
            discountCode: (value) => {
                const requiredValidatorRes = requiredFieldFormValidator(value);
                if (requiredValidatorRes) return requiredValidatorRes;
            }
        }
    });
    type FormValues = typeof form.values;

    const computeOrderSubtotal = (): number => {
        const isExpress = Boolean(getValues('express'));
        const products = (getValues('products') || []) as any[];
        return computeBillableOrderSubtotal(products, isExpress, {
            orderType: getValues('orderType'),
            returnItemsChargeMode: getValues('returnItemsChargeMode')
        });
    };

    const onSubmit = async (values: FormValues) => {
        try {
            const res = await verifyDiscountCode.mutateAsync({
                code: values.discountCode.toUpperCase(),
                orderSubtotal: computeOrderSubtotal(),
            });
            handleVerifyDiscountCode(res);
        } catch (error: any) {
            console.log(error);
            const backendErrors: string[] = error?.response?.data?.errors || [];
            const meta = error?.response?.data?.meta;
            if (backendErrors.includes('backend.errors.discountCodeBelowMinimumSubtotal')) {
                form.setErrors({
                    discountCode: t('DiscountsPage:discountCodes.minimumOrderSubtotalError', {
                        amount: meta?.minimumOrderSubtotal ?? '',
                    })
                });
                return;
            }
            form.setErrors({
                discountCode: t('FormValidator:discountCodeNotFound')
            });
        }
    }

    const handleVerifyDiscountCode = async (verifiedDiscountCode: IDiscountCode) => {
        try {
            if (!verifiedDiscountCode) {
                form.setErrors({
                    discountCode: t('NewOrder:discountCodeNotFound')
                });
                return;
            }
            const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
                discountCodeObj: verifiedDiscountCode,
                products: getValues('products'),
                express: getValues('express'),
                storeDiscounts,
                customerInfo: getValues('customerInfo'),
                previousBuyAndGetProducts: getValues('buyAndGetProducts'),
                orderType: getValues('orderType'),
                returnItemsChargeMode: getValues('returnItemsChargeMode')
            });

            setValue('discountCode', verifiedDiscountCode._id || '');
            setValue('discountCodeObj', verifiedDiscountCode || undefined);
            setValue('products', newOrderProds);
            setValue('buyAndGetProducts', buyAndGetProds);
            onClose();
        } catch (error) {
            console.log(error);
        }
    }

    return (
        <Modal opened={opened} onClose={onClose} title={t('NewOrder:addDiscountCode')}>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Stack>
                    <TextInput
                        label={t('FieldName:code')}
                        placeholder={t('FieldName:code')}
                        description={t('NewOrder:discountCodeWarning')}
                        {...form.getInputProps('discountCode')}
                    />
                    <Flex justify="space-between" gap={'xs'}>
                        <Button
                            color="gray"
                            variant="filled"
                            onClick={onClose}
                            flex={'1 1 auto'}
                            loading={verifyDiscountCode.isPending}
                        >
                            {t('FieldName:cancel')}
                        </Button>
                        <Button type="submit" flex={'1 1 auto'} loading={verifyDiscountCode.isPending}>
                            {t('FieldName:apply')}
                        </Button>
                    </Flex>
                </Stack>
            </form>
        </Modal>
    )
}

export default AddDiscountCodeModal;
